import Image from 'next/image';
import { LocaleDetails } from 'shared/src/types/languages.type';

type Props = {
  lang: LocaleDetails;
  /**
   * False on the search landing: that page *is* home, so linking there invites
   * a click that appears to do nothing. The mark stays purely decorative and
   * keeps its hover spin — no pointer cursor, no focus stop, no announcement.
   */
  linkToHome?: boolean;
};

/**
 * Brand mark (crescent, sun and constellation) that crowns the Region column.
 * Presentational only (no hooks), so both the async server Footer and the
 * FooterClient can render it.
 *
 * Opt-in, not fleet-wide: rendered only where the caller passes `showMark`
 * (search landing, custom pages). It is lazy and below the fold everywhere, so
 * it costs nothing on first paint — but at quality 100 it is a heavier fetch
 * than the fleet needs on every generated page, so pages opt in rather than out.
 */
export function FooterLogo({ lang, linkToHome = true }: Props) {
  // Literal px, not h-24: the root font is 15px here, so rem-based sizes land
  // at 90px and the mark reads a touch small against the columns.
  const box = 'group relative block h-[76px] w-[76px] sm:h-[96px] sm:w-[96px]';

  const mark = (
    <Image
      src="/images/inception-mark.png"
      // Decorative when it is not a link: the hero already carries the
      // wordmark, so announcing it again buys nothing.
      alt={linkToHome ? 'Inception logo' : ''}
      fill
      // Must match the rendered box. The browser multiplies this by DPR and
      // rounds up to the next candidate: 256w at 2x, 384w at 3x — both a
      // healthy oversample of a 96px mark. Inflating it (sizes="256px")
      // silently pulls a 640w render for no visible gain.
      sizes="96px"
      // Knocked back slightly on the navy ground, where the sun otherwise
      // glares. Slow spin on hover is the same callback the mark had in the
      // top bar. The spin is always attached but paused, and only runs on
      // hover, so leaving the mark freezes it at its current angle instead of
      // snapping back to upright.
      className="dark:brightness-90 animate-spin-slow [animation-play-state:paused] group-hover:[animation-play-state:running] motion-reduce:animate-none"
      quality={100}
    />
  );

  if (!linkToHome) {
    return <div className={box}>{mark}</div>;
  }

  return (
    <a href={`/${lang.path}`} aria-label="Inception" className={box}>
      {mark}
    </a>
  );
}
